<h2>
  Office Charges for
  <span class="office-name">{{ getOfficeName(activeOffice) }}</span>
</h2>
<div id="office-margin-header" [class.editing]="isEditingOfficeMargin">
  <h3 i18n="@@office-charges.default-margin">Default margin</h3>
  @if (iAmAdmin && !isEditingOfficeMargin) {
    <button
      id="edit-office-margin"
      class="office-settings-icon-button"
      mat-icon-button
      aria-label="Edit office name"
      (click)="startEditingOfficeMargin()"
    >
      <mat-icon>edit</mat-icon>
    </button>
  }
  @if (!iAmAdmin) {
    <div class="office-settings-icon-button">
      <em
        [ngbPopover]="popContent"
        container="body"
        triggers="mouseenter:mouseleave"
        class="fa fa-exclamation-circle ps-2 text-danger"
      ></em>
      <ng-template
        #popContent
        i18n="@@settings.text-only-office-admin-can-edit"
      >
        Only an Office Admin can edit this section.
      </ng-template>
    </div>
  }

  @if (isEditingOfficeMargin) {
    <div class="flex">
      <button
        class="office-settings-icon-button save"
        mat-icon-button
        aria-label="Save office name"
        id="save-office-name"
        [disabled]="!officeMarginForm.valid"
        (click)="saveOfficeMarginChanges()"
      >
        <mat-icon>done</mat-icon>
      </button>
      <button
        class="office-settings-icon-button"
        mat-icon-button
        aria-label="Stop editing"
        (click)="onDiscardOfficeMarginChanges()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }
</div>
<article id="office-margin-card">
  <form
    id="office-margin-form"
    class="office-settings-form"
    [formGroup]="officeMarginForm"
  >
    <div class="body">
      <div class="form-field-group mb-2">
        <div class="margin-control" style="gap: 4px">
          <mat-form-field appearance="outline" ngDefaultControl required>
            <mat-label i18n="@@office-charges.margin">Margin</mat-label>
            <input
              matInput
              type="text"
              class="input-field"
              aria-label="Margin"
              formControlName="margin"
            />
          </mat-form-field>
          <div class="margin-percent-radio-button">
            <label for="isMarginFlat">$</label>
            <input
              type="radio"
              formControlName="isMarginPercent"
              id="isMarginFlat"
              [value]="false"
            />
          </div>
          <div class="margin-percent-radio-button">
            <label for="isMarginPercent">%</label>
            <input
              type="radio"
              formControlName="isMarginPercent"
              id="isMarginPercent"
              [value]="true"
            />
          </div>
        </div>
        <mat-form-field appearance="outline" ngDefaultControl>
          <mat-label i18n="@@office-charges.minimum">Minimum</mat-label>
          <input
            matInput
            type="text"
            class="input-field"
            aria-label="Minimum"
            formControlName="marginMinFlat"
            maxlength="7"
          />
          @if (isOfficeMarginControlHasError("marginMinFlat", "min")) {
            <mat-error> Minimum </mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline" ngDefaultControl>
          <mat-label i18n="@@office-setting.maximum">Maximum</mat-label>
          <input
            matInput
            type="text"
            class="input-field"
            aria-label="Maximum"
            formControlName="marginMaxFlat"
            maxlength="7"
          />
        </mat-form-field>
        <mat-form-field appearance="outline" ngDefaultControl required>
          <mat-label i18n="@@office-setting.currency">Currency</mat-label>
          <mat-select
            aria-label="Currency"
            formControlName="marginCurrencyCode"
            panelClass="office-settings-panel"
          >
            @for (currency of allCurrencies; track currency) {
              <mat-option
                [value]="currency.currencyCode"
                class="office-settings-select-option"
              >
                {{ currency.currencyCode }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
      <div class="d-flex flex-column errors">
        @if (isOfficeMarginControlHasError("margin", "required")) {
          <p i18n="@@office-setting.margin-value-required">
            Margin value is required
          </p>
        }
        @if (isOfficeMarginControlHasError("margin", "isNumber")) {
          <p i18n="@@office-setting.margin-value-number">
            Margin value must be a number
          </p>
        }
        @if (
          isOfficeMarginControlHasError("margin", "maxDecimalPlaces")
        ) {
          <p i18n="@@office-setting.margin-value-decimal-places">
            Margin value must have at most two decimal places
          </p>
        }
        @if (isOfficeMarginControlHasError("margin", "min")) {
          <p i18n="@@office-setting.margin-value-min">
            Margin value must be greater than 0
          </p>
        }
        @if (isOfficeMarginControlHasError("marginCurrencyCode", "required")) {
          <p i18n="@@office-setting.margin-value-required">
            Margin currency is required
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMinFlat", "isNumber")) {
          <p i18n="@@office-setting.margin-min-number">
            Minimum margin must be a number
          </p>
        }
        @if (
          isOfficeMarginControlHasError("marginMinFlat", "maxDecimalPlaces")
        ) {
          <p i18n="@@office-setting.margin-min-decimal-places">
            Minimum margin must have at most two decimal places
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMinFlat", "min")) {
          <p i18n="@@office-setting.margin-min-min">
            Minimum margin must be greater than or equal to 0
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMinFlat", "max")) {
          <p i18n="@@office-setting.margin-min-max">
            Minimum margin must be less than or equal to 1,000,000
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMaxFlat", "isNumber")) {
          <p i18n="@@office-setting.margin-max-number">
            Maximum margin must be a number
          </p>
        }
        @if (
          isOfficeMarginControlHasError("marginMaxFlat", "maxDecimalPlaces")
        ) {
          <p i18n="@@office-setting.margin-max-decimal-places">
            Maximum margin must have at most two decimal places
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMaxFlat", "min")) {
          <p i18n="@@office-setting.margin-max-min">
            Maximum margin must be greater than or equal to 0 and minimum margin
          </p>
        }
        @if (isOfficeMarginControlHasError("marginMaxFlat", "max")) {
          <p i18n="@@office-setting.margin-max-max">
            Maximum margin must be less than or equal to 1,000,000
          </p>
        }
      </div>
      <p class="mt-2">
        <em class="fa fa-exclamation-circle" style="font-size: 16px"> </em>
        <span i18n="@@office-setting.used-to-create-quotation">
          This information will be used to create a quotation and can be
          adjusted while creating a quotation.
        </span>
      </p>
    </div>
  </form>
</article>
<div id="office-services-header">
  <div class="d-flex align-items-center" style="gap: 16px; margin-bottom: 32px">
    <h3 i18n="@@office-setting.services">Services by Airports</h3>
    @if (iAmAdmin && selectedAirportCharges) {
      <ng-template [ngTemplateOutlet]="addAirportButton"></ng-template>
    }
  </div>
  @if (selectedAirportCharges) {
    <div style="max-width: 410px">
      <p>Select or search an existing airport to view service charges</p>
      <cai-autocomplete
        [width]="410"
        [hideLabel]="true"
        [openDropdownOnClick]="true"
        [options]="airportSelectorOptions"
        (onSelectItem)="onSelectAirport($event?.value)"
        class="office-airport-charge-selector"
        placeholder="Select Airport"
        dropdownMenuClass="office-settings-autocomplete-menu"
      >
      </cai-autocomplete>
    </div>
  }
</div>
@if (!selectedAirportCharges) {
  <article id="no-office-airport-charges">
    <img
      height="150"
      src="/assets/media/icons/no-results.svg"
      loading="lazy"
      alt="no results"
    />
    <h4>No airports with services found</h4>
    @if (iAmAdmin) {
      <p>Please select a new airport and add at least one service</p>
    }
    @if (iAmAdmin) {
      <ng-template [ngTemplateOutlet]="addAirportButton"></ng-template>
    }
  </article>
}
@if (selectedAirportCharges) {
  <article id="office-services">
    <div class="d-flex align-items-center" style="gap: 16px">
      <h4>
        {{ selectedAirportCharges?.airport?.airportCode }} -
        {{ selectedAirportCharges?.airport?.airportName }}
      </h4>
      <mat-select
        [value]="selectedAirportCharges?.currency?.currencyCode"
        class="office-settings-form-field airport-currency-selector"
        panelClass="office-settings-panel office-service-panel"
        (selectionChange)="onSelectAirportChargeCurrency($event.value)"
      >
        @for (currency of allCurrencies; track currency) {
          <mat-option
            [value]="currency.currencyCode"
            class="office-settings-select-option"
          >
            {{ currency.currencyCode }}
          </mat-option>
        }
      </mat-select>
    </div>
    <div class="d-flex align-items-center" style="gap: 26px">
      @if (iAmAdmin) {
        <button
          mat-flat-button
          color="secondary"
          class="office-settings-action-button"
          (click)="createDraftOfficeService()"
          [disabled]="hasDraftCharge"
        >
          <mat-icon>add</mat-icon>
          <span i18n="@@office-setting.add-services">Add Services</span>
        </button>
      }
      <div class="table-filters">
        <mat-select
          aria-label="Type"
          panelClass="office-settings-panel office-service-panel table-filter-panel"
          panelWidth="96px"
          class="office-settings-form-field"
          [class.panel-open]="typeSelect.panelOpen"
          #typeSelect
          [(value)]="chargeFilter.type"
          placeholder="Type"
        >
          @for (enum of OfficeChargeCategory | keyvalue; track enum) {
            <mat-option
              [value]="enum.key"
              class="office-settings-select-option"
            >
              {{ enum.value }}
            </mat-option>
          }
        </mat-select>
        <mat-select
          panelClass="office-settings-panel office-service-panel table-filter-panel"
          panelWidth="192px"
          class="office-settings-form-field"
          [class.panel-open]="uomSelect.panelOpen"
          #uomSelect
          [(value)]="chargeFilter.uom"
          placeholder="UOM"
        >
          @for (enum of OfficeChargeUnitOfMeasure | keyvalue; track enum) {
            <mat-option
              [value]="enum.key"
              class="office-settings-select-option"
            >
              {{ enum.value }}
            </mat-option>
          }
        </mat-select>
        <mat-select
          panelClass="office-settings-panel office-service-panel table-filter-panel"
          panelWidth="160px"
          class="office-settings-form-field"
          [class.panel-open]="commoditySelect.panelOpen"
          #commoditySelect
          [(value)]="chargeFilter.default"
          placeholder="Default"
        >
          @for (enum of commodityType | keyvalue; track enum) {
            <mat-option
              [value]="enum.key"
              class="office-settings-select-option"
            >
              {{ enum.value }}
            </mat-option>
          }
        </mat-select>
      </div>
    </div>
    <div id="divider"></div>
    <table
      id="office-services-table"
      class="table table-hover table-checkable sortable table-responsive office-settings-table"
      matSort
      [matSortActive]="sortState.active"
      [matSortDirection]="sortState.direction"
      (matSortChange)="sortData($event)"
    >
      <thead>
        <tr>
          <th id="col-name" scope="col" mat-sort-header="id.name">
            <span i18n="@office-charges.name">Name</span>
            <span style="color: red">*</span>
          </th>
          <th id="col-buying-rate" scope="col" mat-sort-header="buyingRate">
            <span i18n="@office-charges.buying-rate">Buying Rate</span>
          </th>
          <th id="col-flat-rate" scope="col" mat-sort-header="markupFlatRate">
            <span i18n="@office-charges.markup">Markup</span>
          </th>
          <th id="col-selling-rate" scope="col" mat-sort-header="sellingRate">
            <span i18n="@office-charges.selling-rate">Selling Rate</span>
            <span style="color: red">*</span>
          </th>
          <th id="col-min" scope="col" i18n="@@office-charges.min">Min</th>
          <th id="col-max" scope="col" i18n="@@office-charges.max">Max</th>
          <th id="col-uom" scope="col">
            <span i18n="@office-charges.uon">UOM</span>
            <span style="color: red">*</span>
          </th>
          <th id="col-type" scope="col">
            <span i18n="@@office-charges.type">Type</span>
            <span style="color: red">*</span>
          </th>
          <th id="col-commodity" scope="col" i18n="@@office-charges.default">
            Default
          </th>
          @if (iAmAdmin) {
            <th id="col-actions" scope="col" i18n="@@office-charges.actions">
              Actions
            </th>
          }
          <th id="col-fill"></th>
        </tr>
      </thead>
      @if (!isLoading) {
        <tbody>
          @for (
            service of activeOfficeCharges | paginate: pagination;
            track trackById(i, service);
            let i = $index
          ) {
            <tr [formGroup]="editServicesForm[i]">
              <td headers="col-name">
                @if (!isEditingService[i]) {
                  <span [title]="service.name">{{ service.name }}</span>
                }
                @if (isEditingService[i]) {
                  <input
                    formControlName="name"
                    (mouseover)="onHoverFormField(editServicesForm[i], 'name')"
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                  />
                }
              </td>
              <td headers="col-buying-rate">
                @if (!isEditingService[i]) {
                  <span>{{ service.buyingRate ?? "N.A." }}</span>
                } @else {
                  <input
                    formControlName="buyingRate"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'buyingRate')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                  />
                }
              </td>
              <td headers="col-flat-rate">
                @if (!isEditingService[i]) {
                  @if (service.markupFlatRate) {
                    <span>{{ service.markupFlatRate }}</span>
                  }
                  @if (service.markupPercent) {
                    <span>{{ service.markupPercent * 100 }}%</span>
                  }
                  @if (!service.markupFlatRate && !service.markupPercent) {
                    <span>N.A.</span>
                  }
                } @else {
                  <div class="markup-control">
                    <input
                      formControlName="markup"
                      (mouseover)="
                        onHoverFormField(editServicesForm[i], 'markup')
                      "
                      [matTooltip]="officeServiceTooltipError"
                      [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                    />
      
                    <div class="markup-flat-radio-button">
                      <label for="isMarkupFlat">$</label>
                      <input
                        type="radio"
                        formControlName="isMarkupFlat"
                        id="isMarkupFlat"
                        [value]="true"
                      />
                    </div>
                    <div class="markup-flat-radio-button">
                      <label for="isMarkupPercent">%</label>
                      <input
                        type="radio"
                        formControlName="isMarkupFlat"
                        id="isMarkupPercent"
                        [value]="false"
                      />
                    </div>
                  </div>
                }
              </td>
              <td headers="col-selling-rate">
                @if (!isEditingService[i]) {
                  <span>{{ service.sellingRate ?? "N.A." }}</span>
                } @else {
                  <input
                    *ngIf="isEditingService[i]"
                    formControlName="sellingRate"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'sellingRate')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                  />
                }
              </td>
              <td headers="col-min">
                @if (!isEditingService[i]) {
                  <span>{{ service.markupMinFlat ?? "N.A." }}</span>
                }
                @if (isEditingService[i]) {
                  <input
                    formControlName="markupMinFlat"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'markupMinFlat')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                  />
                }
              </td>
              <td headers="col-max">
                @if (!isEditingService[i]) {
                  <span>{{ service.markupMaxFlat ?? "N.A." }}</span>
                }
                @if (isEditingService[i]) {
                  <input
                    formControlName="markupMaxFlat"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'markupMaxFlat')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                  />
                }
              </td>
              <td headers="col-uom">
                @if (!isEditingService[i]) {
                  <span>{{
                    OfficeChargeUnitOfMeasure[service.unitOfMeasure] ?? "N.A."
                  }}</span>
                }
                @if (isEditingService[i]) {
                  <mat-select
                    formControlName="unitOfMeasure"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'unitOfMeasure')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                    panelClass="office-settings-panel office-service-panel"
                  >
                    @for (
                      enum of OfficeChargeUnitOfMeasure | keyvalue;
                      track enum
                    ) {
                      <mat-option
                        [value]="enum.key"
                        class="office-settings-select-option"
                      >
                        {{ enum.value }}
                      </mat-option>
                    }
                  </mat-select>
                }
              </td>
              <td headers="col-type">
                @if (!isEditingService[i]) {
                  <span>{{
                    OfficeChargeCategory[service.chargeCategory] ?? "N.A."
                  }}</span>
                }
                @if (isEditingService[i]) {
                  <mat-select
                    formControlName="chargeCategory"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'chargeCategory')
                    "
                    placeholder="Type"
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                    panelClass="office-settings-panel office-service-panel"
                  >
                    @for (enum of OfficeChargeCategory | keyvalue; track enum) {
                      <mat-option
                        [value]="enum.key"
                        class="office-settings-select-option"
                      >
                        {{ enum.value }}
                      </mat-option>
                    }
                  </mat-select>
                }
              </td>
              <td headers="col-commodity">
                @if (!isEditingService[i]) {
                  <span>{{
                    service.isDefault
                      ? commodityType[service.defaultCommodity] || "Yes"
                      : "No"
                  }}</span>
                }
                @if (isEditingService[i]) {
                  <mat-checkbox
                    formControlName="isDefault"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'isDefault')
                    "
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                  >
                  </mat-checkbox>
                }
                @if (
                  isEditingService[i] &&
                  editServicesForm?.[i]?.get("isDefault")?.value
                ) {
                  <mat-select
                    style="width: 164px"
                    placeholder="Goods Type"
                    formControlName="defaultCommodity"
                    (mouseover)="
                      onHoverFormField(editServicesForm[i], 'defaultCommodity')
                    "
                    [disabled]="true"
                    [matTooltip]="officeServiceTooltipError"
                    [matTooltipDisabled]="
                      officeServiceTooltipError.length === 0
                    "
                    panelClass="office-settings-panel office-service-panel"
                    class="show-errors-asap"
                  >
                    @for (enum of commodityType | keyvalue; track enum) {
                      <mat-option
                        [value]="enum.key"
                        class="office-settings-select-option"
                      >
                        {{ enum.value }}
                      </mat-option>
                    }
                  </mat-select>
                }
              </td>
              @if (iAmAdmin) {
                <td headers="col-actions">
                  <div class="service-actions">
                    @if (!isEditingService[i]) {
                      <button
                        class="office-settings-icon-button"
                        mat-icon-button
                        color="primary"
                        aria-label="Edit office service"
                        (click)="startEditingOfficeService(i)"
                      >
                        <mat-icon>edit</mat-icon>
                      </button>
                    }
                    @if (!isEditingService[i] && canDeleteService[i]) {
                      <button
                        class="office-settings-icon-button"
                        mat-icon-button
                        color="primary"
                        aria-label="Delete office service"
                        (click)="onDeleteServiceButtonClick(i)"
                      >
                        <mat-icon>delete</mat-icon>
                      </button>
                    }
                    @if (isEditingService[i]) {
                      <button
                        class="office-settings-icon-button save"
                        mat-icon-button
                        aria-label="Save office service"
                        [disabled]="!editServicesForm[i].valid"
                        (click)="saveOfficeServiceChanges(i)"
                      >
                        <mat-icon>done</mat-icon>
                      </button>
                    }
                    @if (isEditingService[i]) {
                      <button
                        class="office-settings-icon-button"
                        mat-icon-button
                        aria-label="Stop editing"
                        (click)="discardOfficeServiceChanges(i)"
                      >
                        <mat-icon>close</mat-icon>
                      </button>
                    }
                  </div>
                </td>
              }
              <td headers="col-fill"></td>
            </tr>
          }
        </tbody>
      }
      <tfoot>
        <tr>
          <td colspan="9">
            <div id="office-services-table-pagination">
              <pagination-controls
                [previousLabel]="PREVIOUS_LABEL"
                [nextLabel]="NEXT_LABEL"
                (pageChange)="onPageChanged($event)"
              ></pagination-controls>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
    <kt-loading-component [isLoading]="isLoading"></kt-loading-component>
  </article>
}
<ng-template #addAirportButton>
  <button
    mat-flat-button
    color="secondary"
    class="office-settings-action-button"
    (click)="onAddAirportButtonClick()"
  >
    <mat-icon>add</mat-icon>
    <span i18n="@@office-setting.add-services">Add New Airport</span>
  </button>
</ng-template>
